// @/utils/userInfoUtils.js

/**
 * 用户信息工具类
 */
class UserInfoUtils {
  /**
   * 获取当前登录用户的工号
   * @returns {string} 用户工号
   */
  static getUserCode() {
    try {
      // 优先从localStorage获取
      const userCode = window.localStorage.getItem('userCode');
      if (userCode) {
        return userCode;
      }
      
      // 如果localStorage中没有，尝试从Cookie获取（兼容旧版本）
      const Cookies = require('js-cookie');
      const savedUserCode = Cookies.get('userCode');
      return savedUserCode || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取当前登录用户的用户名
   * @returns {string} 用户名
   */
  static getUsername() {
    try {
      const username = window.localStorage.getItem('username');
      if (username) {
        return username;
      }
      
      const Cookies = require('js-cookie');
      const savedUsername = Cookies.get('userName');
      return savedUsername || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取用户token
   * @returns {string} token
   */
  static getToken() {
    try {
      return window.localStorage.getItem('token') || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取用户部门
   * @returns {string} 部门
   */
  static getDepartment() {
    try {
      return window.localStorage.getItem('department') || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取用户职位
   * @returns {string} 职位
   */
  static getPosition() {
    try {
      return window.localStorage.getItem('position') || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取用户页面查看权限
   * @returns {string} urlPermission 模块编码串（1项目 2合同 3生产 4产品 5部件 6交付）
   * - 从未存过（首次进入 / 老版本数据）→ 兜底 '1'
   * - 后端显式下发空串（"urlPermission": ""）→ 如实返回 ''，代表无任何模块权限
   * - 正常下发（如 '2'、'2345'）→ 原样返回
   */
  static getUrlPermission() {
    try {
      const value = window.localStorage.getItem('urlPermission');
      // ⚠️ 不能用 `|| '1'` 兜底：空字符串 '' 是 falsy，会被兜底成 '1'（=全部权限），
      //    于是后端下发「空权限」时菜单反而把「项目管理」显示出来。
      //    只有「压根没存过」（null / undefined）才走兜底，存了 '' 就如实返回 ''。
      if (value === null || value === undefined) return '1';
      return value;
    } catch (error) {
      return '1';
    }
  }

  /**
   * 获取用户是否为管理员
   * @returns {boolean} 是否为管理员
   */
  static isAdmin() {
    try {
      const isAdmin = window.localStorage.getItem('isAdmin');
      return isAdmin === '1';
    } catch (error) {
      return false;
    }
  }

  /**
   * 获取“返回主页”应跳转的路由（与 UserSettings.goBack 的权限判断一致）
   * @returns {string} 路由路径
   * - 管理员 → 项目主页 /homepage/projects
   * - 有权限的普通用户 → urlPermission 编码串里第一个有权限的模块主页
   *   （1项目 2合同 3生产 4产品 5部件 6交付，如 '2345' → 合同主页；'6' → 交付主页）
   * - ⚠️ 无任何模块权限（'' / '0' / 不匹配任何编码）→ '/homepage'（空框架页）
   *     停在父路由上，router-view 没有子路由可匹配，内容区就是空的，不会加载任何主页
   */
  static getDefaultHomeRoute() {
    if (this.isAdmin()) return '/homepage/projects';

    const permission = this.getUrlPermission();
    // ✅ 没有任何模块权限 → 停在 /homepage 空框架页，不加载任何主页
    if (!permission || permission === '0') return '/homepage';

    // 按编码顺序取第一个有权限的模块主页
    const moduleRoutes = [
      ['1', '/homepage/projects'],
      ['2', '/homepage/contracts'],
      ['3', '/homepage/workOrders'],
      ['4', '/homepage/products'],
      ['5', '/homepage/parts'],
      ['6', '/homepage/delivery']
    ];
    const hit = moduleRoutes.find(item => permission.indexOf(item[0]) !== -1);
    return hit ? hit[1] : '/homepage';
  }

  /**
   * 获取用户某个页面的列偏好（登录时从后端带下来的）
   * @param {string} pageType 页面标识，如 'productProgress'
   * @returns {Object|null} 列偏好数据 { columns: [{prop, visible}] } 或 null
   */
  static getColumnPreference(pageType) {
    try {
      const raw = window.localStorage.getItem('columnPref_' + pageType);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[UserInfoUtils] 读取列偏好失败', pageType, e);
      return null;
    }
  }

  /**
   * 更新用户某个页面的列偏好到本地缓存
   * @param {string} pageType 页面标识，如 'productProgress'
   * @param {Array} columns 列配置数组 [{prop, visible}]
   */
  static setColumnPreference(pageType, columns) {
    try {
      window.localStorage.setItem('columnPref_' + pageType, JSON.stringify(columns));
    } catch (e) {
      console.warn('[UserInfoUtils] 保存列偏好失败', pageType, e);
    }
  }

  /**
   * 获取用户全部信息（新增方法）
   * @returns {Object} 用户完整信息对象
   */
  static getAllUserInfo() {
    return {
      userCode: this.getUserCode(),
      username: this.getUsername(),
      token: this.getToken(),
      department: this.getDepartment(),
      position: this.getPosition(),
      isAdmin: this.isAdmin(),
      urlPermission: this.getUrlPermission()
    };
  }

  /**
   * 检查用户是否已登录
   * @returns {boolean} 是否已登录
   */
  static isLoggedIn() {
    const token = this.getToken();
    const userCode = this.getUserCode();
    return !!(token && userCode);
  }

  /**
   * 清除用户登录信息
   */
  static clearUserInfo() {
    // 清除 localStorage
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('userCode');
    window.localStorage.removeItem('username');
    window.localStorage.removeItem('department');
    window.localStorage.removeItem('position');
    window.localStorage.removeItem('isAdmin');
    window.localStorage.removeItem('urlPermission');

    // 清除列偏好
    const prefPages = ['productProgress', 'workOrderProgress', 'contractProgress', 'projectProgress', 'abnormalProgress', 'partsProgress',
      'orderProgress', 'deliveryProgress',
      'productProgress_m', 'contractProgress_m', 'workOrderProgress_m', 'projectProgress_m', 'abnormalProgress_m', 'partsProgress_m',
      'orderProgress_m', 'deliveryProgress_m'];
    prefPages.forEach(key => window.localStorage.removeItem('columnPref_' + key));
    
    // 清除 Cookie（兼容旧版本）
    const Cookies = require('js-cookie');
    Cookies.remove('userCode');
    Cookies.remove('userName');
    Cookies.remove('userPwd');
  }
}

export default UserInfoUtils;